import { mapFillStyleTo } from '@ff-os/rendering-shared/coloring';
import { PaintTarget } from '@ff-os/rendering-shared/targets';
import { Json } from '@ff-os/shared-json/Json';
import { defaultFonts } from '@ff-os/shared-utils/constants/default-fonts';
import { Size } from '@ff-os/shared-utils/types/Size';
import { degreesToRadians } from '@ff-os/shared-utils/units';
import { GiftJson } from '@ff-scene-plugins/gift-shared/GiftJson';
import { measureText, Text, TextToMeasure } from 'ff-canvas';
import { TextVariableInput } from './TextVariableInput';
import { TextVariableOptions } from './TextVariableOptions';

export const textToFfCanvas = (
    variable: GiftJson.Variable<TextVariableOptions>,
    input: TextVariableInput,
    palette: Json.Palette,
    target: PaintTarget,
    jwt: string | null
): Text => {

    const computedMetrics = {
        height: input.computedHeight,
        fontSize: input.computedFontSize
    };

    if (!computedMetrics.height || !computedMetrics.fontSize) {
        const { height, fontSize } = computeHeightAndFontSize(input, variable);
        computedMetrics.height = height;
        computedMetrics.fontSize = fontSize;
    }

    const angle = degreesToRadians(variable.options.rotation);

    const heightOffset = 0.5 * (variable.height - computedMetrics.height);
    const offsetX = Math.sin(angle) * heightOffset;
    const offsetY = Math.cos(angle) * heightOffset;

    return {

        id: variable.id,
        type: 'text',
        round: false,

        x: variable.x - offsetX,
        y: variable.y + offsetY,
        width: variable.width,
        height: computedMetrics.height,
        zIndex: variable.zIndex,
        angle: angle,

        scaleX: 1,
        scaleY: 1,
        alpha: 1,

        content: input.content,
        textAlign: 'center',
        fontFamily: input.fontFamily,
        fontStyle: input.fontStyle,
        fontWeight: input.fontWeight,
        fontSize: computedMetrics.fontSize,
        fallbackFontFamilies: defaultFonts,
        fillStyle: mapFillStyleTo(target, input, variable.options, palette),

        shadow: false,
        shadowBlur: input.shadowBlur,
        shadowColor: input.shadowColor,
        shadowOffsetX: input.shadowOffsetX,
        shadowOffsetY: input.shadowOffsetY,

        stroke: false,
        strokeSize: input.strokeSize,
        strokeStyle: input.strokeStyle,

        custom: {
            watch: [],
            values: {}
        }
    };
};

export const computeHeightAndFontSize = (
    input: TextVariableInput,
    variable: GiftJson.Variable<TextVariableOptions>
) => {

    const baseFontSize = 10;
    const { width: baseWidth } = metricsOf(input, baseFontSize);
    const fontSize = fontSizeOf(baseFontSize, baseWidth, variable);
    const { height } = metricsOf(input, fontSize);

    return {
        height: height,
        fontSize: fontSize
    };
};

const fontSizeOf = (
    baseFontSize: number,
    baseWidth: number,
    variable: GiftJson.Variable<TextVariableOptions>
) => {
    const computedFontSize = variable.width / baseWidth * baseFontSize;
    return Math.min(computedFontSize, variable.height);
};

let canvas: HTMLCanvasElement;

const metricsOf = (input: TextVariableInput, fontSize: number): Size => {

    if (process.env.REACT_APP_TO_BUILD === 'website-server') {

        return {
            width: 1,
            height: 1
        };

    } else {

        if (!canvas) {
            // @ts-ignore
            canvas = document.createElement('canvas');
        }

        const text: TextToMeasure = {
            content: input.content,
            fontFamily: input.fontFamily,
            fontStyle: input.fontStyle,
            fontWeight: input.fontWeight,
            fallbackFontFamilies: defaultFonts,
            fontSize: fontSize
        };

        return measureText(text, canvas);
    }
};